import { waitForOpenAIAdClick } from './openai-ads';
import { hasWalkthroughHandoff, isWalkthroughDocument } from './walkthrough-handoff';
import { startGoogleAds } from './google-ads';
import { startFreebuff } from './freebuff';
import { analyticsSchemaVersion } from '../../shared/funnel-analytics';
import { release } from '../../shared/analytics-safety';
import { clearFunnelCookie } from './funnel-context';
import posthog from 'posthog-js';
import { startBrowserErrors, setErrorTrackingEnabled } from './browser-errors';
import { startAdvertising, stopAdvertising } from './advertising';
import { applyAnalyticsPreference } from './analytics-preference';
import { POSTHOG_PRIVACY_CONFIG } from './posthog-privacy';
import { prepareCampaign } from './campaign-attribution';
import { isPublicReplayPage, stopPublicReplay } from './public-replay';
import { isEmbedDocument } from './embed-document';

const projectToken = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN;
const host = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;
const appEnvironment = import.meta.env.MODE;
let captureAdLanding = () => {};

function registerAppEnvironment(): void {
  if (projectToken && host) posthog.register({ app_environment: appEnvironment, analytics_schema_version: analyticsSchemaVersion, release, is_test: appEnvironment !== 'production' || posthog.get_property('is_test') === true || posthog.get_property('internal_or_test_user') === true });
}

if (typeof window !== 'undefined' && !isWalkthroughDocument && projectToken && host) {
  posthog.init(projectToken, {
    api_host: host,
    ui_host: 'https://us.posthog.com',
    defaults: '2026-01-30',
    ...POSTHOG_PRIVACY_CONFIG,
    // Embeds run on third-party pages: count views without setting cookies there.
    ...(isEmbedDocument ? { persistence: 'memory' as const } : {}),
    // Replay needs project recording configuration, even though feature flags stay off.
    advanced_disable_flags: !(typeof window !== 'undefined'
      && isPublicReplayPage(appEnvironment, new URL(window.location.href))
      && !(navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl),
    advanced_disable_feature_flags: true,
    advanced_disable_feature_flags_on_first_load: true,
  });
  registerAppEnvironment();
  if (typeof window !== 'undefined') startBrowserErrors();
  if (typeof window !== 'undefined') {
    captureAdLanding = prepareCampaign(posthog, window.location.pathname, window.location.search);
  }
} else if (typeof window !== 'undefined' && !isWalkthroughDocument && import.meta.env.DEV) {
  const missingVariable = projectToken
    ? 'VITE_PUBLIC_POSTHOG_HOST'
    : 'VITE_PUBLIC_POSTHOG_PROJECT_TOKEN';

  console.error(
    new Error(
      `${missingVariable} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once ${missingVariable} is configured`,
    ),
  );
}

export function setAnalyticsEnabled(enabled: boolean): void {
  if (isWalkthroughDocument) {
    if (!enabled) stopAdvertising();
    return;
  }
  applyAnalyticsPreference(posthog, enabled);
  if (!enabled) { clearFunnelCookie(); stopPublicReplay(); captureAdLanding = () => {}; stopAdvertising(); }
  setErrorTrackingEnabled(enabled);
  registerAppEnvironment();
}

export function resetAnalyticsIdentity(): void {
  clearFunnelCookie();
  stopPublicReplay();
  stopAdvertising();
  captureAdLanding = () => {};
  posthog.reset();
  registerAppEnvironment();
}

// Call after resolving the account's analytics preference, before identifying the visitor.
export async function trackAdLanding(anonymousLanding: boolean): Promise<void> {
  if (isWalkthroughDocument) return;
  captureAdLanding();
  void startAdvertising(() => posthog.has_opted_out_capturing(), anonymousLanding);
  await waitForOpenAIAdClick();
}

export function trackSettingsViewed(): void {
  posthog.capture('settings_viewed', { surface: 'web' });
}

export function trackUserSignedOut(): void {
  posthog.capture(
    'user_signed_out',
    { surface: 'web' },
    { send_instantly: true, transport: 'sendBeacon' },
  );
  resetAnalyticsIdentity();
}

export default posthog;

// Bootstrap every SPA entry; the server resolves privacy before enabling the hosted tag.
// A plain Freebuff landing may measure its public funnel. Imported walkthroughs stay isolated.
// On that landing the server, GPC and preference cookie resolve consent without initializing PostHog.
if (typeof window !== 'undefined' && !hasWalkthroughHandoff && !isEmbedDocument) void startFreebuff(() => !isWalkthroughDocument && posthog.has_opted_out_capturing());

if (typeof window !== 'undefined' && !isWalkthroughDocument && !isEmbedDocument) void startGoogleAds(() => posthog.has_opted_out_capturing());
